import React, { KeyboardEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import AppIcon, { AppIconType } from "../AppIcon/AppIcon";
import AppText, { FormLabel } from "../AppText/AppText";
import LoadingIndicator from "../../Shared/LoadingIndicator/LoadingIndicator";
import Flex from "../../Layout/Flex";
import { appTheme } from "../../../theme/Theme";

export interface AppInputProps {
	type?: string;
	value?: string | number;
	onChange?: any;
	onBlurText?: () => void;
	onChangeText?: (text: string) => void;
	onChangeNumber?: (val: number, e: React.FormEvent<HTMLInputElement>) => void;
	name?: string;
	errors?: any;
	label?: string | string[];
	icon?: AppIconType;
	loading?: boolean;
	disabled?: boolean;
	placeholder?: string;
	className?: string;
	eventOnKeyDown?: any;
	field?: string;
	margin?: string | number;
	padding?: string;
	containerBackground?: string;
	background?: string;
	borderColor?: string;
	width?: string;
	containerWidth?: string;
	containerMargin?: string;
	containerRef?: any;
	onFocus?: any;
	pattern?: any;
	/**
	 * Min value, only applicable if type
	 * is number and onChangeNumber is used
	 */
	min?: string | number;
	/**
	 * Max value, only applicable if type
	 * is number and onChangeNumber is used
	 */
	max?: string | number;
	step?: string;
	readOnly?: boolean;
	defaultValue?: string;
	description?: string;
	/**
	 * Will shows asterisk next to label which indicates that field
	 * is mandatory. Will not add any validation logic!
	 */
	mandatory?: boolean;

	/**
	 * If text in input should be selected when input is focused.
	 *
	 * This will from user perspective make it slightly easier to
	 * replace the value if input if of such kind where it makes sense.
	 */
	selectOnFocus?: boolean;

	/**
	 * Convenient callback to handle when user presses Enter
	 * on keydown.
	 */
	onEnter?: (e: KeyboardEvent) => void;

	onClick?: (e: any) => void;

	/**
	 * Optional tooltip ℹ️
	 */
	tooltip?: string | React.ReactNode;

	/**
	 * Optional knowledge base tag to open knowledge base sidebar
	 */
	knowledgeBaseTag?: string;

	/**
	 * Optional custom tooltip text for knowledge base trigger
	 */
	knowledgeBaseTooltip?: string;

	/**
	 * Optional knowledge base tag that only shows on hover (reduces visual clutter)
	 */
	showKnowledgeBaseForTagOnHover?: string;

	autoComplete?: string;

	/**
	 * Focus the input when it mounts.
	 */
	autoFocus?: boolean;

	/**
	 * If top skip attempt to translate label.
	 * Should in most cases not be needed.
	 */
	skipLabelTranslation?: boolean;

	textAlign?: "center" | "left" | "right";

	prefix?: string;

	isValid?: boolean;
}

const AppInput = React.forwardRef<HTMLInputElement, AppInputProps>(
	(
		{
			width,
			name,
			type,
			value,
			onChange,
			onBlurText,
			onChangeText,
			onChangeNumber,
			errors,
			label,
			icon,
			disabled,
			placeholder,
			className, // TODO
			eventOnKeyDown,
			field, // TODO
			margin,
			padding,
			containerBackground,
			background,
			borderColor,
			containerMargin,
			containerWidth,
			containerRef,
			onFocus,
			pattern,
			min,
			max,
			step,
			readOnly,
			defaultValue,
			description,
			mandatory,
			selectOnFocus,
			onEnter,
			tooltip,
			knowledgeBaseTag,
			knowledgeBaseTooltip,
			showKnowledgeBaseForTagOnHover,
			autoComplete = "off",
			autoFocus,
			skipLabelTranslation = false,
			loading,
			textAlign,
			prefix,
			onClick,
			isValid = true,
		},
		ref
	) => {
		const { t } = useTranslation();
		const innerInputRef = useRef<HTMLInputElement>(null);
		const theRef = ref !== undefined ? ref : innerInputRef;

		function handleOnFocus(event: React.FocusEvent<HTMLInputElement>) {
			if (selectOnFocus) {
				if (ref !== undefined) {
					throw new Error(
						"Cannot set selectOnFocus when ref is set, you need to handle focus on your own"
					);
				}
				innerInputRef.current?.select();
			}
			onFocus && onFocus(event);
		}

		function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
			if (e.key === "Enter") {
				onEnter && onEnter(e);
			}

			eventOnKeyDown && eventOnKeyDown(e);
		}

		return (
			<InputContainer
				containerWidth={containerWidth}
				containerMargin={containerMargin}
				containerRef={containerRef}
				onClick={onClick}
			>
				{label && (
					<FormLabel
						tooltip={tooltip}
						knowledgeBaseTag={knowledgeBaseTag}
						knowledgeBaseTooltip={knowledgeBaseTooltip}
						showKnowledgeBaseForTagOnHover={showKnowledgeBaseForTagOnHover}
					>
						{skipLabelTranslation ? label : t(label as any)}
						{mandatory ? " *" : ""}
					</FormLabel>
				)}
				<InputWrapper
					width={containerWidth}
					margin={margin}
					padding={padding}
					containerBackground={containerBackground}
					borderColor={borderColor}
					background={background}
					style={{
						border: !isValid ? "1px solid hsl(var(--danger))" : undefined,
						backgroundColor: !isValid ? "#FFF5F5" : undefined,
					}}
				>
					{loading && <LoadingIndicator size="s" className="loading-indicator" showText={false} />}

					{!prefix && icon && !loading && (
						<AppIcon icon={icon} color={"#999"} margin="0 7px 0 1px" />
					)}
					{prefix && (
						<Flex
							bg={appTheme.colors.COLOR_DIVIDER_GRAY}
							width="fit-content"
							padding="2px 0"
							borderRadius="2px"
							margin="0 6px 0 0"
							className="prefix-ctn"
						>
							{icon && (
								<AppIcon
									icon={icon}
									size={"14px"}
									color={appTheme.colors.COLOR_GRAY_TEXT}
									margin="0 1px 0 3px"
								/>
							)}

							<AppText size="xs" weight="medium" margin="0 4px">
								{prefix}
							</AppText>
						</Flex>
					)}
					<InputStyles
						pattern={pattern}
						min={min}
						max={max}
						step={step}
						onFocus={handleOnFocus}
						width={width}
						ref={theRef}
						name={name}
						textAlign={textAlign}
						onChange={(e) => {
							if (onChange) onChange(e);
							if (onChangeText) onChangeText(e.target.value);
							if (onChangeNumber) {
								let { value, min, max } = e.target;
								let validatedValue = Number(value);

								if (min !== undefined && max !== "") {
									validatedValue = Math.max(validatedValue, Number(min));
								}
								if (max !== undefined && max !== "") {
									validatedValue = Math.min(validatedValue, Number(max));
								}

								onChangeNumber(validatedValue, e);
							}
						}}
						onBlur={onBlurText}
						value={value}
						type={type}
						disabled={disabled}
						placeholder={placeholder && t(placeholder as any)}
						onKeyDown={handleKeyDown}
						defaultValue={defaultValue}
						readOnly={readOnly}
						autoComplete={autoComplete}
						autoFocus={autoFocus}
					/>
				</InputWrapper>
				{description && (
					<AppText margin="0.35rem 0 0 0.15rem" size="xs" color="grayText">
						{t(description as any)}
					</AppText>
				)}
				<AppText margin="0.35rem 0 0 0.15rem" size="xs" color="danger">
					{errors && t(errors as any)}
				</AppText>
			</InputContainer>
		);
	}
);

export default AppInput;

const InputWrapper = styled.div<AppInputProps>`
	display: flex;
	max-width: ${(props) => (props.containerWidth ? props.containerWidth : "100%")};
	padding: ${(props) => (props.padding ? props.padding : "0.5rem")};
	border-radius: 2px;
	background: ${(props) =>
		props.containerBackground ? props.containerBackground : props.theme.colors.COLOR_WHITE};
	height: 2.3125rem;
	margin: ${(props) => (props.margin ? props.margin : "0")};
	border: 1px solid
		${(props) => (props.borderColor ? props.borderColor : props.theme.colors.INPUT_BORDER)};

	/* https://css-tricks.com/almanac/selectors/f/focus-within/ */
	&:focus-within {
		border-color: ${(props) => props.theme.colors.INPUT_BORDER_FOCUSED};
		background: ${(props) =>
			props.background && props.background !== "transparent"
				? props.background
				: props.theme.colors.COLOR_WHITE};
		padding: ${(props) => (props.padding && props.padding !== "0" ? props.padding : "0.5rem")};

		.prefix-ctn {
			background-color: ${(props) => props.theme.colors.COLOR_LIGHT_BLUE2};
		}

		svg {
			color: black !important;
		}
	}

	input {
		background: ${(props) =>
			props.background ? props.background : props.theme.colors.COLOR_WHITE};
		font-size: 0.9375rem;
	}

	&:has(input:disabled) {
		background: ${(props) => props.theme.colors.COLOR_LIGHT_GRAY};
	}

	input:disabled {
		background: transparent;
		color: ${(props) => props.theme.colors.COLOR_GRAY_TEXT};
		cursor: not-allowed;
	}

	.loading-indicator {
		max-width: 20px;
		margin-right: 5px;
	}
`;

const InputStyles = styled.input<{ textAlign?: string }>`
	border: none;
	width: 100%;
	font-size: ${(props) => props.theme.fontSizes.FONT_SIZE_M};
	line-height: 0.9375rem;

	${({ textAlign }) => (textAlign ? `text-align: ${textAlign};` : "")}
`;

const InputContainer = styled.div<AppInputProps>`
	margin: ${(props) => (props.containerMargin ? props.containerMargin : "0 0 1.25rem 0")};
	width: ${(props) => (props.containerWidth ? props.containerWidth : "100%")};
`;
